﻿import { useEffect, useRef } from 'react'

const isTouchDevice = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(hover: none), (pointer: coarse)').matches

export default function CustomCursor() {
  const dotRef  = useRef(null)
  const ringRef = useRef(null)
  const glowRef = useRef(null)
  const s = useRef({
    pos:      { x: -300, y: -300 },
    ring:     { x: -300, y: -300 },
    glow:     { x: -300, y: -300 },
    clicking: false,
    hovering: false,
    visible:  false,
  })

  useEffect(() => {
    if (isTouchDevice()) return
    const dot  = dotRef.current
    const ring = ringRef.current
    const glow = glowRef.current
    if (!dot || !ring || !glow) return

    const lerp = (a, b, n) => a + (b - a) * n

    const onMove = (e) => {
      const c = s.current
      c.pos.x = e.clientX
      c.pos.y = e.clientY
      if (!c.visible) {
        c.visible = true
        c.ring.x = e.clientX; c.ring.y = e.clientY
        c.glow.x = e.clientX; c.glow.y = e.clientY
        dot.style.opacity  = '1'
        ring.style.opacity = '1'
        glow.style.opacity = '1'
      }
    }
    const onLeave = () => {
      s.current.visible = false
      dot.style.opacity  = '0'
      ring.style.opacity = '0'
      glow.style.opacity = '0'
    }
    const onDown = () => { s.current.clicking = true }
    const onUp   = () => { s.current.clicking = false }

    const INTERACTIVE = 'a, button, .proj-card, input, textarea, select, [data-hover]'
    const onOver = (e) => {
      if (!s.current.hovering && e.target.closest(INTERACTIVE)) {
        s.current.hovering = true
        ring.style.width  = '58px'
        ring.style.height = '58px'
        ring.style.borderColor = 'rgba(34,211,238,0.65)'
        ring.style.background  = 'rgba(6,182,212,0.07)'
        dot.style.width   = '4px'
        dot.style.height  = '4px'
        dot.style.background = '#22d3ee'
      }
    }
    const onOut = (e) => {
      const leaving = e.target.closest(INTERACTIVE)
      const entering = e.relatedTarget?.closest(INTERACTIVE)
      if (leaving && !entering && s.current.hovering) {
        s.current.hovering = false
        ring.style.width  = '40px'
        ring.style.height = '40px'
        ring.style.borderColor = 'rgba(255,255,255,0.22)'
        ring.style.background  = 'transparent'
        dot.style.width   = '6px'
        dot.style.height  = '6px'
        dot.style.background = '#fff'
      }
    }

    let raf
    const tick = () => {
      const c = s.current
      c.ring.x = lerp(c.ring.x, c.pos.x, 0.13)
      c.ring.y = lerp(c.ring.y, c.pos.y, 0.13)
      c.glow.x = lerp(c.glow.x, c.pos.x, 0.045)
      c.glow.y = lerp(c.glow.y, c.pos.y, 0.045)

      const sc = c.clicking ? 0.78 : 1

      dot.style.transform  = `translate(${c.pos.x - 3}px,${c.pos.y - 3}px)`
      ring.style.transform = `translate(${c.ring.x - 20}px,${c.ring.y - 20}px) scale(${sc})`
      glow.style.transform = `translate(${c.glow.x - 220}px,${c.glow.y - 220}px)`

      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)

    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseleave', onLeave)
    window.addEventListener('mousedown', onDown)
    window.addEventListener('mouseup', onUp)
    document.addEventListener('mouseover', onOver)
    document.addEventListener('mouseout', onOut)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseleave', onLeave)
      window.removeEventListener('mousedown', onDown)
      window.removeEventListener('mouseup', onUp)
      document.removeEventListener('mouseover', onOver)
      document.removeEventListener('mouseout', onOut)
    }
  }, [])

  return (
    <>
      {/* Ambient glow — large slow-following orb creating cursor lighting */}
      <div ref={glowRef} style={{
        position: 'fixed', top: 0, left: 0, zIndex: 9994,
        width: 440, height: 440,
        background: 'radial-gradient(circle, rgba(6,182,212,0.13) 0%, rgba(6,182,212,0.04) 45%, transparent 70%)',
        borderRadius: '50%',
        pointerEvents: 'none',
        opacity: 0,
        willChange: 'transform',
        mixBlendMode: 'screen',
        transition: 'opacity 0.4s ease',
      }} />

      {/* Ring */}
      <div ref={ringRef} style={{
        position: 'fixed', top: 0, left: 0, zIndex: 9998,
        width: 40, height: 40,
        border: '1.5px solid rgba(255,255,255,0.22)',
        borderRadius: '50%',
        pointerEvents: 'none',
        opacity: 0,
        willChange: 'transform',
        transition: 'width 0.28s cubic-bezier(.25,.46,.45,.94), height 0.28s cubic-bezier(.25,.46,.45,.94), border-color 0.28s, background 0.28s, opacity 0.3s, transform 0.06s linear',
      }} />

      {/* Dot */}
      <div ref={dotRef} style={{
        position: 'fixed', top: 0, left: 0, zIndex: 9999,
        width: 6, height: 6,
        background: '#fff',
        borderRadius: '50%',
        pointerEvents: 'none',
        opacity: 0,
        willChange: 'transform',
        mixBlendMode: 'difference',
        transition: 'width 0.2s, height 0.2s, background 0.2s, opacity 0.3s',
      }} />
    </>
  )
}
